import React, { ReactNode, useState } from "react";
import Header from "@cloudscape-design/components/header";
import { VehicleAlarms } from "../data/data";
import Table, { TableProps } from "@cloudscape-design/components/table";
import { useCollection } from "@cloudscape-design/collection-hooks";
import {
  CollectionPreferences,
  CollectionPreferencesProps,
} from "@cloudscape-design/components";
import Pagination from "@cloudscape-design/components/pagination";
import Box from "@cloudscape-design/components/box";
import Button from "@cloudscape-design/components/button";
import TextFilter from "@cloudscape-design/components/text-filter";
import { Link } from "react-router-dom";
import Icon from "@cloudscape-design/components/icon";
import StatusIndicator from "@cloudscape-design/components/status-indicator";

interface CellProps {
 
  Alarm : string | number;
}
const AlarmCell: React.FC<CellProps> = ({ Alarm }) => {
    let statusType: "success" | "error" | "warning" | "info" = "info"; // status types (status indicator cloudscape)

    // Conditional rendering logic
    if (Alarm === 28) {
        statusType = 'success';
    } else if (Alarm === 27) {
        statusType = 'error';
    } else if (Alarm === 23) {
        statusType = 'warning';
    } else {
        statusType = 'info';
    }

    return (
        <StatusIndicator type={statusType}>
            {statusType === 'success' ? 'Operational Normal' : 
             statusType === 'error' ? 'Battery Low' :
             statusType === 'warning' ? 'No GPS' :
             statusType === 'info' ? 'Data Error' :
             'Unknown Status'}
        </StatusIndicator>
    );
};

const getFilterCounterText = (count: number = 0) =>
  `${count} ${count === 1 ? "match" : "matches"}`;

export interface VariationTableProps {
  VehicleMaster: VehicleAlarms[];
}



const columnDefinitions: TableProps<VehicleAlarms>["columnDefinitions"] = [
  {
    header: "ID",
    cell: ({ id }) => id,
    sortingField: "ID",
    width: "5%",
  },
  
  {
    header: "Vehicle Code",
    cell: ({ VehicleCode }) => (
      <Link to={`/vehicles/alarms/${VehicleCode}`}>
        {VehicleCode}
      </Link>
    ),
    sortingField: "VehicleCode",
    width: 100,
  },
 
  {
    header: "Alarm ",
    cell: AlarmCell,
    sortingField: "Alarm",
    width: 120,
  },
  {
    header: "Timestamps ",
    cell: ({ Timestamps }) => Timestamps,
    sortingField: "Timestamps",
    width: 100,
  },
  
];

const EmptyState = ({
  title,
  subtitle,
  action,
}: {
  title: string;
  subtitle: string;
  action: ReactNode;
}) => {
  return (
    <Box textAlign='center' color='inherit'>
      <Box variant='strong' textAlign='center' color='inherit'>
        {title}
      </Box>
      <Box variant='p' padding={{ bottom: "s" }} color='inherit'>
        {subtitle}
      </Box>
      {action}
    </Box>
  );
};

const getHeaderCounterText = (
  items: readonly VehicleAlarms[] = [],
  selectedItems: readonly VehicleAlarms[] = [],
  displayedItemsCount: number = 0
) => {
  return selectedItems && selectedItems.length > 0
    ? `(${selectedItems.length}/${displayedItemsCount}/${items.length})`
    : `(${displayedItemsCount}/${items.length})`;
};

export default function VehicleList({ VehicleMaster }: VariationTableProps) {
  const [preferences, setPreferences] = useState<
    CollectionPreferencesProps["preferences"]
  >({ pageSize: 10 });
  const {
    items,
    filterProps,
    actions,
    filteredItemsCount,
    paginationProps,
    collectionProps,
  } = useCollection<VehicleAlarms>(VehicleMaster, {
    filtering: {
      noMatch: (
        <EmptyState
          title='No matches'
          subtitle='We can’t find a match.'
          action={
            <Button onClick={() => actions.setFiltering("")}>
              Clear filter
            </Button>
          }
        />
      ),
    },

    pagination: { pageSize: preferences?.pageSize },
    sorting: { defaultState: { sortingColumn: columnDefinitions[0] } },
    selection: {},
  });
  return (
    <>
      
      <Table<VehicleAlarms>
        {...collectionProps}
        items={items}
        columnDefinitions={columnDefinitions}
        stickyHeader={true}
        resizableColumns={false}
        variant='container'
        ariaLabels={{
          selectionGroupLabel: "Items selection",
          itemSelectionLabel: ({ selectedItems }, item) => {
            const isItemSelected = selectedItems.filter(
              (i) => i.id === item.id
            ).length;
            return `${item.id} is ${isItemSelected ? "" : "not "}selected`;
          },
          tableLabel: "Master Vehicle table",
        }}
        header={
          <Header
            variant="h1"
            counter={getHeaderCounterText(
              VehicleMaster,
              collectionProps.selectedItems,
              items.length
            )}
          >
            Alarms
          </Header>
        }
        pagination={<Pagination {...paginationProps} />}
        filter={
          <TextFilter
            {...filterProps}
            filteringPlaceholder='Find Vehicle'
            countText={getFilterCounterText(filteredItemsCount)}
          />
        }
        preferences={
          <CollectionPreferences
            preferences={preferences}
            pageSizePreference={{
              title: "select page size",
              options: [
                { value: 10, label: "10 resources" },
                { value: 20, label: "20 resources" },
                { value: 50, label: "50 resources" },
                { value: 100, label: "100 resources" },
              ],
            }}
            onConfirm={({ detail }) => setPreferences(detail)}
            title='preference'
            confirmLabel='Confrim'
            cancelLabel='Cancel'
          />
        }
        // selectionType="multi"
      />
    </>
  );
}
